import React, { useMemo } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import FiltersSidebar, {
  FilterSection,
  FilterCheckboxRow,
  FilterCheckboxList,
} from './FiltersSidebar';
import type { Customer, CustomerKind } from '../services/customerService';
import type { UseCustomerFiltersResult } from '../hooks/useCustomerFilters';

interface CustomerFiltersSidebarProps {
  filters: UseCustomerFiltersResult;
  /** Source customer list — used to derive the available tag options. */
  customers: Customer[];
  /** Whether the panel is collapsed. Controlled by the page (via the existing
   *  hamburger toggle in the header), not by an in-panel chevron. */
  collapsed: boolean;
}

// Tag pills are styled distinctly from the generic chips (saturated brand
// background, count badge), so they live here rather than in FiltersSidebar.
const useStyles = makeStyles({
  tagPill: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.padding('4px', '10px'),
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.borderRadius('14px'),
    fontSize: '12px',
    cursor: 'pointer',
    userSelect: 'none',
    ...shorthands.border('1px', 'solid', 'transparent'),
    '&:hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  // Active state distinguished via an outline ring + brand-pressed bg, since
  // the resting state is now fully saturated.
  tagPillActive: {
    backgroundColor: tokens.colorBrandBackgroundPressed,
    boxShadow: `0 0 0 2px ${tokens.colorBrandBackground2}`,
  },
  tagList: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    ...shorthands.gap('6px'),
  },
  tagPillCount: {
    marginLeft: '6px',
    fontSize: '11px',
    fontWeight: 600,
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    ...shorthands.padding('1px', '6px'),
    ...shorthands.borderRadius('10px'),
    minWidth: '16px',
    textAlign: 'center',
    lineHeight: '14px',
  },
});

const KIND_OPTIONS: { value: CustomerKind; label: string }[] = [
  { value: 'individual', label: 'Individual' },
  { value: 'contact', label: 'Contact' },
  { value: 'company', label: 'Company' },
];

/**
 * Left-rail filter panel for the Customers table. Live-applied (no Apply
 * button); state persists across sessions via useCustomerFilters.
 *
 * Tag option list is derived from the loaded customer list so it reflects
 * what's actually present in the data — no separate dictionary needed.
 */
const CustomerFiltersSidebar: React.FC<CustomerFiltersSidebarProps> = ({
  filters,
  customers,
  collapsed,
}) => {
  const styles = useStyles();
  const { state, setField, clearAll, activeFilterCount, hasActiveFilters } = filters;

  // Tag → record-count map. Counts how many customers in the loaded list
  // carry each tag; surfaced inside the pill so staff can see at a glance
  // which tags actually have meaningful coverage before clicking.
  const tagOptions = useMemo(() => {
    const counts = new Map<string, number>();
    customers.forEach((c) => {
      (c.tags || []).forEach((t) => counts.set(t, (counts.get(t) || 0) + 1));
    });
    return Array.from(counts.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [customers]);

  // Per-kind counts for the Customer type checkbox rows. Treats missing
  // customerKind as 'individual' to match useCustomerFilters.applyFilters.
  const kindCounts = useMemo(() => {
    const m = new Map<CustomerKind, number>();
    for (const c of customers) {
      const k = (c.customerKind || 'individual') as CustomerKind;
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [customers]);

  const toggleKind = (kind: CustomerKind) => {
    const has = state.customerKinds.includes(kind);
    setField(
      'customerKinds',
      has ? state.customerKinds.filter((k) => k !== kind) : [...state.customerKinds, kind],
    );
  };

  const toggleTag = (tag: string) => {
    const has = state.tags.includes(tag);
    setField('tags', has ? state.tags.filter((t) => t !== tag) : [...state.tags, tag]);
  };

  return (
    <FiltersSidebar
      collapsed={collapsed}
      activeFilterCount={activeFilterCount}
      hasActiveFilters={hasActiveFilters}
      onClearAll={clearAll}
      onRequestClose={() => setField('sidebarCollapsed', true)}
    >
      <FilterSection label="Customer type">
        <FilterCheckboxList>
          {KIND_OPTIONS.map((opt) => (
            <FilterCheckboxRow
              key={opt.value}
              checked={state.customerKinds.includes(opt.value)}
              onChange={() => toggleKind(opt.value)}
              label={opt.label}
              count={kindCounts.get(opt.value) ?? 0}
            />
          ))}
        </FilterCheckboxList>
      </FilterSection>

      <FilterSection
        label="Tags"
        emptyHint={tagOptions.length === 0 ? 'No tags in current data' : undefined}
      >
        <div className={styles.tagList}>
          {tagOptions.map(([tag, count]) => {
            const active = state.tags.includes(tag);
            return (
              <span
                key={tag}
                className={`${styles.tagPill} ${active ? styles.tagPillActive : ''}`}
                onClick={() => toggleTag(tag)}
                role="button"
                aria-pressed={active}
              >
                {tag}
                <span className={styles.tagPillCount}>{count}</span>
              </span>
            );
          })}
        </div>
      </FilterSection>
    </FiltersSidebar>
  );
};

export default CustomerFiltersSidebar;
